import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { ShoppingCart, Search, User, Menu, X, ChevronDown, ChevronRight, Heart } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '../ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_841fbfa7-1a20-4a56-bfc0-aa4a083c4873/artifacts/mbzov694_Logo-Rani_final.webp';

export const Header = () => {
  const {
    language,
    setLanguage,
    cartItemsCount,
    customer,
    logout,
    t,
    categories,
    getLocalizedText,
  } = useApp();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [expandedCategories, setExpandedCategories] = useState([]);

  // Build category tree
  const buildCategoryTree = (cats, parentId = null) => {
    return cats
      .filter(c => (c.parent_id || null) === parentId)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      .map(c => ({
        ...c,
        children: buildCategoryTree(cats, c.id)
      }));
  };

  const categoryTree = buildCategoryTree(categories);
  const headerCategories = categories.filter(cat => cat.show_in_header && !cat.parent_id);

  const toggleMobileCategory = (catId) => {
    setExpandedCategories(prev => 
      prev.includes(catId) 
        ? prev.filter(id => id !== catId)
        : [...prev, catId]
    );
  };

  // Get children of a category
  const getChildren = (parentId) => {
    return categories.filter(c => c.parent_id === parentId).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  };

  // Load favorites count
  useEffect(() => {
    const updateFavoritesCount = () => {
      const favorites = JSON.parse(localStorage.getItem('rani_favorites') || '[]');
      setFavoritesCount(favorites.length);
    };
    updateFavoritesCount();
    window.addEventListener('storage', updateFavoritesCount);
    window.addEventListener('favoritesUpdated', updateFavoritesCount);
    return () => {
      window.removeEventListener('storage', updateFavoritesCount);
      window.removeEventListener('favoritesUpdated', updateFavoritesCount);
    };
  }, []);

  const languages = [
    { code: 'ro', name: 'RO' },
    { code: 'en', name: 'EN' },
  ];

  // Recursive mobile category item component
  const MobileCategoryItem = ({ cat, level = 0 }) => {
    const children = getChildren(cat.id);
    const hasChildren = children.length > 0;
    const isExpanded = expandedCategories.includes(cat.id);

    if (!hasChildren) {
      return (
        <Link
          to={`/shop?category=${cat.id}`}
          onClick={() => setMobileMenuOpen(false)}
          className={`text-base font-medium hover:text-gold transition-colors block py-2`}
          style={{ paddingLeft: `${level * 16}px` }}
        >
          {getLocalizedText(cat.name)}
        </Link>
      );
    }

    return (
      <Collapsible open={isExpanded} onOpenChange={() => toggleMobileCategory(cat.id)}>
        <div className="flex items-center justify-between" style={{ paddingLeft: `${level * 16}px` }}>
          <Link
            to={`/shop?category=${cat.id}`}
            onClick={() => setMobileMenuOpen(false)}
            className="text-base font-medium hover:text-gold transition-colors py-2 flex-1"
          >
            {getLocalizedText(cat.name)}
          </Link>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="p-1 h-auto">
              <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </Button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent>
          <div className="border-l border-gray-200 ml-2">
            {children.map(child => (
              <MobileCategoryItem key={child.id} cat={child} level={level + 1} />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  };

  // Recursive desktop category menu item
  const DesktopCategoryItem = ({ cat }) => {
    const children = getChildren(cat.id);
    const hasChildren = children.length > 0;

    if (!hasChildren) {
      return (
        <DropdownMenuItem asChild>
          <Link to={`/shop?category=${cat.id}`} className="w-full">
            {getLocalizedText(cat.name)}
          </Link>
        </DropdownMenuItem>
      );
    }

    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <Link 
            to={`/shop?category=${cat.id}`} 
            className="flex-1"
            onClick={(e) => e.stopPropagation()}
          >
            {getLocalizedText(cat.name)}
          </Link>
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          <DropdownMenuItem asChild>
            <Link to={`/shop?category=${cat.id}`} className="w-full font-medium">
              {language === 'ro' ? 'Vezi tot' : 'View all'} {getLocalizedText(cat.name)}
            </Link>
          </DropdownMenuItem>
          {children.map(child => (
            <DesktopCategoryItem key={child.id} cat={child} />
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
      {/* Top bar */}
      <div className="bg-surface border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-2 flex justify-between items-center text-sm">
          <div className="hidden sm:flex items-center gap-4 text-muted-foreground">
            <span>{language === 'ro' ? 'Livrare Gratuită peste 349 RON' : 'Free Shipping over 349 RON'}</span>
          </div>
          <div className="flex items-center gap-4 ml-auto">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 px-2 text-sm font-medium" data-testid="language-selector">
                  {language.toUpperCase()} <ChevronDown className="ml-1 h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={language === lang.code ? 'bg-purple-50' : ''}
                    data-testid={`lang-${lang.code}`}
                  >
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4">
        <div className="flex items-center justify-between">
          {/* Mobile menu - bigger purple button */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button 
                variant="ghost" 
                size="icon" 
                className="bg-primary hover:bg-purple-600 text-white rounded-lg w-10 h-10"
                data-testid="mobile-menu-btn"
              >
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 overflow-y-auto">
              <nav className="flex flex-col gap-2 mt-8">
                <Link
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-lg font-medium hover:text-gold transition-colors py-2"
                >
                  {t('home')}
                </Link>
                <Link
                  to="/shop"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-lg font-medium hover:text-gold transition-colors py-2"
                >
                  {t('shop')}
                </Link>
                
                {/* Hierarchical Categories */}
                <div className="border-t pt-2 mt-2">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
                    {language === 'ro' ? 'Categorii' : 'Categories'}
                  </p>
                  {categoryTree.map((cat) => (
                    <MobileCategoryItem key={cat.id} cat={cat} />
                  ))}
                </div>
                
                <div className="border-t pt-2 mt-2">
                  <Link
                    to="/blog"
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-lg font-medium hover:text-gold transition-colors block py-2"
                  >
                    {t('blog')}
                  </Link>
                  <Link
                    to="/contact"
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-lg font-medium hover:text-gold transition-colors block py-2"
                  >
                    {t('contact')}
                  </Link>
                </div>
              </nav>
            </SheetContent>
          </Sheet>

          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="logo-link">
            <img
              src={LOGO_URL}
              alt="Rani Store"
              className="h-12 sm:h-16 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <Link
              to="/"
              className="text-sm font-medium text-foreground hover:text-gold transition-colors duration-300"
              data-testid="nav-home"
            >
              {t('home')}
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="text-sm font-medium hover:text-gold transition-colors duration-300"
                  data-testid="nav-shop"
                >
                  {t('shop')} <ChevronDown className="ml-1 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56">
                <DropdownMenuItem asChild>
                  <Link to="/shop" className="w-full font-medium">{language === 'ro' ? 'Vezi tot' : 'View All'}</Link>
                </DropdownMenuItem>
                {categoryTree.map((cat) => (
                  <DesktopCategoryItem key={cat.id} cat={cat} />
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {/* Categories marked for header display - with subcategory dropdowns */}
            {headerCategories.map((cat) => {
              const children = getChildren(cat.id);
              const hasChildren = children.length > 0;
              
              if (!hasChildren) {
                return (
                  <Link
                    key={cat.id}
                    to={`/shop?category=${cat.id}`}
                    className="text-sm font-medium text-foreground hover:text-gold transition-colors duration-300"
                    data-testid={`nav-cat-${cat.slug}`}
                  >
                    {getLocalizedText(cat.name)}
                  </Link>
                );
              }
              
              return (
                <DropdownMenu key={cat.id}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="text-sm font-medium hover:text-gold transition-colors duration-300"
                      data-testid={`nav-cat-${cat.slug}`}
                    >
                      {getLocalizedText(cat.name)} <ChevronDown className="ml-1 h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56">
                    <DropdownMenuItem asChild>
                      <Link to={`/shop?category=${cat.id}`} className="w-full font-medium">
                        {language === 'ro' ? 'Vezi tot' : 'View All'} {getLocalizedText(cat.name)}
                      </Link>
                    </DropdownMenuItem>
                    {children.map(child => (
                      <DesktopCategoryItem key={child.id} cat={child} />
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })}
            <Link
              to="/blog"
              className="text-sm font-medium text-foreground hover:text-gold transition-colors duration-300"
              data-testid="nav-blog"
            >
              {t('blog')}
            </Link>
            <Link
              to="/contact"
              className="text-sm font-medium text-foreground hover:text-gold transition-colors duration-300"
              data-testid="nav-contact"
            >
              {t('contact')}
            </Link>
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2 sm:gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="hover:text-gold"
              onClick={() => navigate('/search')}
              data-testid="search-btn"
            >
              <Search className="h-5 w-5" />
            </Button>

            {/* Wishlist/Favorites */}
            <Button
              variant="ghost"
              size="icon"
              className="relative hover:text-gold"
              onClick={() => navigate('/favorites')}
              data-testid="favorites-btn"
            >
              <Heart className="h-5 w-5" />
              {favoritesCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {favoritesCount}
                </span>
              )}
            </Button>

            {customer ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="hover:text-gold" data-testid="user-menu-btn">
                    <User className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link to="/account">{t('account')}</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={logout}>{t('logout')}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                className="hover:text-gold"
                onClick={() => navigate('/login')}
                data-testid="login-btn"
              >
                <User className="h-5 w-5" />
              </Button>
            )}

            <Button
              variant="ghost"
              size="icon"
              className="relative hover:text-gold"
              onClick={() => navigate('/cart')}
              data-testid="cart-btn"
            >
              <ShoppingCart className="h-5 w-5" />
              {cartItemsCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-gold text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {cartItemsCount}
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};
